
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from './apiClient';
import { useSubscription } from './SubscriptionContext';
import Modal from './Modal'; 
import NotificationModal from './NotificationModal'; 
import { usePermissions } from './PermissionsContext'; 
import PermissionDeniedModal from './components/PermissionDeniedModal'; 

const componentTranslations = {
    ja: {
        mainTitle: "ご契約内容",
        currentPlan: "現在のプラン: {{plan}}",
        invoiceCount: "今月の請求書発行数: {{count}} / {{limit}}",
        invoiceOverage: "プラン超過分: {{count}}件",
        billingPeriod: "対象期間: {{startDate}}～{{endDate}}",
        changePlan: "プラン変更",
        currentPlanLabel: "現在のプラン",
        trialPlanName: "トライアル",
        freePlanName: "フリー",
        litePlanName: "ライト",
        standardPlanName: "スタンダード",
        businessPlanName: "ビジネス",
        freePlanFeature1: "請求書アップロード (月5件まで)",
        freePlanFeature2: "予約済み請求書",
        litePlanFeature1: "請求書アップロード (月30件まで)",
        litePlanFeature2: "予約済み請求書",
        litePlanFeature3: "顧客アップロード",
        litePlanFeature4: "顧客削除",
        standardPlanFeature1: "請求書アップロード (月100件まで)",
        standardPlanFeature2: "予約済み請求書",
        standardPlanFeature3: "顧客アップロード",
        standardPlanFeature4: "顧客削除",
        standardPlanFeature5: "チーム管理",
        businessPlanFeature1: "請求書アップロード (月300件まで)",
        businessPlanFeature2: "予約済み請求書",
        businessPlanFeature3: "顧客アップロード",
        businessPlanFeature4: "顧客削除",
        businessPlanFeature5: "チーム管理",
        planChangeSuccess: "プランが変更されました。",
        planChangeError: "プランの変更に失敗しました。お手数ですが、時間をおいて再度お試しください。",
        modalTitle: "プラン変更の確認",
        resultModalTitleSuccess: "プラン変更完了",
        resultModalTitleError: "エラー",
        upgradeMessage: "プランを{{planName}}に変更します。よろしいですか？",
        downgradeMessage: "{{date}}からのプランを{{planName}}に変更します。よろしいですか？",
        creditCardRequiredTitle: "クレジットカードの登録が必要です",
        creditCardRequiredMessage: "プランを変更するにはクレジットカードを登録してください",
        cancellation: {
            title: "解約について",
            description: "BillWaveを解約すると、契約期間終了後はサービスをご利用いただけなくなります。",
            points: [
                "契約終了時に最終利用料金を請求します",
                "所属するすべてのユーザーが利用停止となります",
                "解約後90日間はデータ閲覧のみ可能です",
                "期間経過後はアカウントが完全解約となります"
            ],
            button: "解約手続きへ",
            modalTitle: "解約確認",
            currentPlanLabel: "現在のプラン",
            endDateLabel: "契約終了予定日",
            estimatedAmountLabel: "今月の請求予定額",
            restrictionsTitle: "解約後の制限",
            restrictions: [
                "請求書発行不可",
                "CSVアップロード不可",
                "顧客登録不可",
                "所属ユーザー全員停止",
                "90日間は閲覧のみ可能"
            ],
            checkboxLabel: "解約後の内容を理解しました",
            confirmButton: "解約申請する",
            cancelButton: "キャンセル"
        },
        canceling: "解約処理中...",
        cancelSuccess: "解約手続きが完了しました。サポート期間終了までデータ閲覧が可能です。",
        cancelError: "解約処理中にエラーが発生しました。",
        accountStatusLabel: "アカウント状態",
        statusActive: "有効",
        statusSuspended: "解約済み（サポート期間中）",
        statusUnpaidSuspended: "利用停止中（未払い）",
        statusCanceling: "解約処理中（最終精算待ち）",
        reactivateButton: "利用を再開する",
        reactivateConfirm: "利用を再開しますか？通常通り請求書の発行が可能になります。",
        reactivateSuccess: "ご利用を再開しました。",
    },
    en: {
        mainTitle: "Subscription",
        currentPlan: "Current Plan: {{plan}}",
        invoiceCount: "Invoices this month: {{count}} / {{limit}}",
        invoiceOverage: "Plan Overage: {{count}}",
        billingPeriod: "Billing Period: {{startDate}} to {{endDate}}",
        changePlan: "Change Plan",
        currentPlanLabel: "Current Plan",
        trialPlanName: "Trial",
        freePlanName: "Free",
        litePlanName: "Lite",
        standardPlanName: "Standard",
        businessPlanName: "Business",
        freePlanFeature1: "Invoice Upload (up to 5/month)",
        freePlanFeature2: "Scheduled Invoices",
        litePlanFeature1: "Invoice Upload (up to 30/month)",
        litePlanFeature2: "Scheduled Invoices",
        litePlanFeature3: "Customer Upload",
        litePlanFeature4: "Customer Delete",
        standardPlanFeature1: "Invoice Upload (up to 100/month)",
        standardPlanFeature2: "Scheduled Invoices",
        standardPlanFeature3: "Customer Upload",
        standardPlanFeature4: "Customer Delete",
        standardPlanFeature5: "Team Management",
        businessPlanFeature1: "Invoice Upload (up to 300/month)",
        businessPlanFeature2: "Scheduled Invoices",
        businessPlanFeature3: "Customer Upload",
        businessPlanFeature4: "Customer Delete",
        businessPlanFeature5: "Team Management",
        planChangeSuccess: "Plan changed successfully.",
        planChangeError: "Failed to change plan. Please try again later.",
        modalTitle: "Confirm Plan Change",
        resultModalTitleSuccess: "Plan Change Complete",
        resultModalTitleError: "Error",
        upgradeMessage: "Are you sure you want to change your plan to {{planName}}?",
        downgradeMessage: "Are you sure you want to change your plan to {{planName}} starting from {{date}}?",
        creditCardRequiredTitle: "Credit Card Required",
        creditCardRequiredMessage: "Please register a credit card to change your plan.",
        cancellation: {
            title: "About Cancellation",
            description: "If you cancel BillWave, you will not be able to use the service after the contract period ends.",
            points: [
                "Final usage fees will be charged at the end of the period",
                "All associated users will be deactivated",
                "Read-only access is available for 90 days after cancellation",
                "The account will be completely deleted after the period"
            ],
            button: "Go to Cancellation",
            modalTitle: "Cancellation Confirmation",
            currentPlanLabel: "Current Plan",
            endDateLabel: "Scheduled End Date",
            estimatedAmountLabel: "Estimated Final Bill",
            restrictionsTitle: "Restrictions after Cancellation",
            restrictions: [
                "Cannot issue invoices",
                "Cannot upload CSVs",
                "Cannot register customers",
                "All team members suspended",
                "90-day read-only period"
            ],
            checkboxLabel: "I understand the consequences of cancellation",
            confirmButton: "Submit Cancellation",
            cancelButton: "Back"
        },
        canceling: "Processing Cancellation...",
        cancelSuccess: "Cancellation successful. You have read-only access until the support period ends.",
        cancelError: "An error occurred during cancellation.",
        accountStatusLabel: "Account Status",
        statusActive: "Active",
        statusSuspended: "Canceled (Support Period)",
        statusUnpaidSuspended: "Suspended (Unpaid)",
        statusCanceling: "Canceling (Pending Final Billing)",
        reactivateButton: "Reactivate Subscription",
        reactivateConfirm: "Are you sure you want to reactivate your subscription? You will be able to upload invoices as usual.",
        reactivateSuccess: "Subscription reactivated successfully.",
    },
};

const getBillingPeriod = (subscriptionStartDateStr) => {
    if (!subscriptionStartDateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(subscriptionStartDateStr);
    const anchorDay = startDate.getDate();
    const getAnchorDateFor = (year, month, day) => {
        const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
        const validDay = Math.min(day, lastDayOfMonth);
        return new Date(year, month, validDay);
    };
    const currentMonthAnchor = getAnchorDateFor(today.getFullYear(), today.getMonth(), anchorDay);
    let periodStart;
    if (today.getTime() >= currentMonthAnchor.getTime()) {
        periodStart = currentMonthAnchor;
    } else {
        let prevMonth = today.getMonth() - 1;
        let prevMonthYear = today.getFullYear();
        if (prevMonth < 0) {
            prevMonth = 11;
            prevMonthYear--;
        }
        periodStart = getAnchorDateFor(prevMonthYear, prevMonth, anchorDay);
    }
    let nextPeriodStartMonth = periodStart.getMonth() + 1;
    let nextPeriodStartYear = periodStart.getFullYear();
    if (nextPeriodStartMonth > 11) {
        nextPeriodStartMonth = 0;
        nextPeriodStartYear++;
    }
    const nextPeriodStartDate = getAnchorDateFor(nextPeriodStartYear, nextPeriodStartMonth, anchorDay);
    const periodEnd = new Date(nextPeriodStartDate);
    periodEnd.setDate(periodEnd.getDate() - 1);
    return { periodStart, periodEnd };
};


const Subscription = () => {
  const { t, i18n } = useTranslation();
  const [translationsLoaded, setTranslationsLoaded] = useState(false);
  const {
    plan: currentPlan,
    setPlan: setCurrentPlan,
    invoiceCount,
    setInvoiceCount,
    invoiceOverage,
    setInvoiceOverage,
    subscriptionStartDate,
    setSubscriptionStartDate,
    nextApplicablePlan, 
    setNextApplicablePlan, 
    accountStatus, 
    setAccountStatus, 
    loading,
    hasPaymentMethod,
    paymentMethodLoading,
  } = useSubscription();
  
  const { permissions, isAdmin } = usePermissions();
  const [isPermissionDeniedModalOpen, setPermissionDeniedModalOpen] = useState(false);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationModalContent, setNotificationModalContent] = useState({ title: '', message: '' });

  // Cancellation UI States
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [hasUnderstoodCancellation, setHasUnderstoodCancellation] = useState(false);

  useEffect(() => {
    Object.entries(componentTranslations).forEach(([lang, resources]) => {
        i18n.addResourceBundle(lang, 'translation', resources, true, true);
      });
      setTranslationsLoaded(true);
  }, [i18n]);

  const plans = {
    trial: { name: t('trialPlanName'), price: 0, invoiceLimit: Infinity, overageUnitPrice: 0, features: [], rank: 0 },
    free: { name: t('freePlanName'), price: 0, invoiceLimit: 5, overageUnitPrice: 120, features: [t('freePlanFeature1'), t('freePlanFeature2')], rank: 0 },
    lite: { name: t('litePlanName'), price: 1000, invoiceLimit: 30, overageUnitPrice: 100, features: [t('litePlanFeature1'), t('litePlanFeature2'), t('litePlanFeature3'), t('litePlanFeature4')], rank: 1 },
    standard: { name: t('standardPlanName'), price: 3000, invoiceLimit: 100, overageUnitPrice: 60, features: [t('standardPlanFeature1'), t('standardPlanFeature2'), t('standardPlanFeature3'), t('standardPlanFeature4'), t('standardPlanFeature5')], rank: 2 },
    business: { name: t('businessPlanName'), price: 8000, invoiceLimit: 300, overageUnitPrice: 30, features: [t('businessPlanFeature1'), t('businessPlanFeature2'), t('businessPlanFeature3'), t('businessPlanFeature4'), t('businessPlanFeature5')], rank: 3 },
  };

  const handlePlanChangeClick = (newPlan) => {
    if (!isAdmin && !permissions.settings?.canChangePlan) {
      setPermissionDeniedModalOpen(true);
      return;
    }

    const isUpgrade = plans[newPlan]?.rank > plans[currentPlan]?.rank;

    if (currentPlan === 'free' && isUpgrade && !hasPaymentMethod) {
        setNotificationModalContent({
          title: t('creditCardRequiredTitle'),
          message: t('creditCardRequiredMessage'),
        });
        setIsNotificationModalOpen(true);
        return;
      }

    if (newPlan === currentPlan && newPlan === nextApplicablePlan) return;
    setSelectedPlan(newPlan); 
    setIsConfirmModalOpen(true); 
  };

  const confirmPlanChange = async () => {
    if (!selectedPlan) return;
    closeConfirmModal();

    try {
        const response = await apiClient.post('/v1/subscription', { plan: selectedPlan });
        if (!response.ok) throw new Error('Failed to update plan on the server');
        
        const data = await response.json();

        setCurrentPlan(data.plan);
        setNextApplicablePlan(data.nextApplicablePlan);
        setInvoiceCount(data.invoiceCount);
        setInvoiceOverage(data.invoiceOverage);
        setSubscriptionStartDate(data.subscriptionStartDate);

        setNotificationModalContent({
            title: t('resultModalTitleSuccess'),
            message: t('planChangeSuccess'),
        });
        setIsNotificationModalOpen(true);

    } catch (error) { 
        console.error('Plan change error:', error);
        setNotificationModalContent({
            title: t('resultModalTitleError'),
            message: t('planChangeError'),
        });
        setIsNotificationModalOpen(true);
    }
  };

  const handleCancelRequest = () => {
      if (!isAdmin) {
          setPermissionDeniedModalOpen(true);
          return;
      }
      setIsCancelModalOpen(true);
  };

  const confirmCancelSubscription = async () => {
    if (!hasUnderstoodCancellation) return;
    setIsCancelModalOpen(false);
    try {
      const response = await apiClient.post('/v1/cancel');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to cancel');
      setAccountStatus(data.status);
      setNotificationModalContent({ title: "解約申請完了", message: t('cancelSuccess') });
      setIsNotificationModalOpen(true);
    } catch (error) {
      console.error('Cancellation error:', error.message);
      alert(t('cancelError') + ": " + error.message);
    }
  };

  const handleReactivate = async () => {
    if (!isAdmin) {
      setPermissionDeniedModalOpen(true);
      return;
    }
    if (!window.confirm(t('reactivateConfirm'))) return;

    try {
      const response = await apiClient.post('/v1/reactivate');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to reactivate');
      setAccountStatus(data.status);
      alert(t('reactivateSuccess'));
      window.location.reload(); 
    } catch (error) {
      console.error('Reactivation error:', error.message);
      alert("Error: " + error.message);
    }
  };

  const closeConfirmModal = () => {
      setIsConfirmModalOpen(false);
      setSelectedPlan(null);
  };

  const closeNotificationModal = () => {
    setIsNotificationModalOpen(false);
  };

  const getBillingPeriodString = () => {
    const period = getBillingPeriod(subscriptionStartDate);
    if (!period) return null;
    const { periodStart, periodEnd } = period;
    const format = (date) => i18n.language === 'ja' ? `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日` : date.toLocaleDateString('en-US');
    return `${format(periodStart)}～${format(periodEnd)}`;
};

const getNextBillingDateString = () => {
    const period = getBillingPeriod(subscriptionStartDate);
    if (!period) return null;
    const { periodEnd } = period;
    const nextBillingDate = new Date(periodEnd);
    nextBillingDate.setDate(nextBillingDate.getDate() + 1);
    const format = (date) => i18n.language === 'ja' ? `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日` : date.toLocaleDateString('en-US');
    return format(nextBillingDate);
};


  if (loading || !translationsLoaded || paymentMethodLoading) {
    return <div className="container mx-auto p-4">Loading...</div>;
  }

  if (!currentPlan || !plans[currentPlan]) {
    return <div className="container mx-auto p-4">Error loading subscription details. Please try again later.</div>;
  }

  const currentPlanDetails = plans[currentPlan];
  const overageCharge = invoiceOverage * currentPlanDetails.overageUnitPrice;
  const estimatedAmount = currentPlanDetails.price + overageCharge;
  const billingPeriodString = getBillingPeriodString();

  const isUpgrade = selectedPlan && plans[selectedPlan]?.rank > currentPlanDetails.rank;
  let confirmModalMessage = "";
  if(selectedPlan) {
    const planName = plans[selectedPlan].name;
    if(isUpgrade) {
        confirmModalMessage = t('upgradeMessage', { planName });
    } else {
        confirmModalMessage = t('downgradeMessage', { planName, date: getNextBillingDateString() });
    }
  }

  const getAccountStatusDisplay = () => {
      switch (accountStatus) {
          case 'suspended': return t('statusUnpaidSuspended');
          case 'suspended_support_only': return t('statusSuspended');
          case 'canceling': return t('statusCanceling');
          default: return t('statusActive');
      }
  };

  const getAccountStatusMessage = () => {
      switch (accountStatus) {
          case 'suspended': return t('suspended_message');
          case 'suspended_support_only': return t('cancelSuccess');
          case 'canceling': return "解約を申し込みました。対象期間の終了時に最終精算が行われます。";
          default: return "";
      }
  };

  const selectablePlanKeys = ['free', 'lite', 'standard', 'business'];

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">{t('mainTitle')}</h1>
      <PermissionDeniedModal isOpen={isPermissionDeniedModalOpen} onClose={() => setPermissionDeniedModalOpen(false)} />

      {accountStatus && accountStatus !== 'active' && (
          <div className={`mb-6 p-4 border-l-4 shadow ${accountStatus === 'suspended' ? 'bg-red-50 border-red-500 text-red-700' : 'bg-yellow-50 border-yellow-500 text-yellow-700'}`}>
              <p className="font-bold">{t('accountStatusLabel')}: {getAccountStatusDisplay()}</p>
              <p className="text-sm mt-1 mb-3">{getAccountStatusMessage()}</p>
              {isAdmin && accountStatus !== 'suspended' && (
                  <button
                    onClick={handleReactivate}
                    className="bg-yellow-600 text-white font-bold py-2 px-4 rounded hover:bg-yellow-700 transition shadow"
                  >
                    {t('reactivateButton')}
                  </button>
              )}
          </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div className="bg-white shadow-md rounded p-4">
          <h2 className="text-xl font-semibold mb-3">ご契約プラン</h2>
          <p>現在の契約プラン： {currentPlanDetails.name}</p>
          <p>次回適用予定プラン： {nextApplicablePlan && plans[nextApplicablePlan] ? plans[nextApplicablePlan].name : 'なし'}</p>
          <p>今月の請求書発行数: {invoiceCount} / {currentPlanDetails.invoiceLimit === Infinity ? '無制限' : currentPlanDetails.invoiceLimit}</p>
          <p>プラン上限： {currentPlanDetails.invoiceLimit === Infinity ? '無制限' : `${currentPlanDetails.invoiceLimit}件`}</p>
          <p>プラン超過分： {invoiceOverage}件</p>
          {billingPeriodString && <p>対象期間: {billingPeriodString}</p>}
        </div>

        <div className="bg-white shadow-md rounded p-4">
          <h2 className="text-xl font-semibold mb-3">今月の請求予定金額</h2>
          <p>プラン基本料金： {currentPlanDetails.price.toLocaleString()}円（税別）</p>
          <p>超過分従量課金： {invoiceOverage}件 × {currentPlanDetails.overageUnitPrice}円 = {overageCharge.toLocaleString()}円（税別）</p>
          <p>今月の請求予定金額： {estimatedAmount.toLocaleString()}円（税別）</p>
          {billingPeriodString && <p>対象期間: {billingPeriodString}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {selectablePlanKeys.map((planKey) => (
          <div key={planKey} className="border rounded-lg p-4 bg-white shadow-sm">
            <h3 className="text-xl font-semibold mb-2">{plans[planKey].name}</h3>
            <ul className="list-disc list-inside mb-4 text-sm text-gray-600">
              {plans[planKey].features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
            <button
              onClick={() => handlePlanChangeClick(planKey)}
              disabled={(currentPlan === planKey && nextApplicablePlan === planKey) || accountStatus !== 'active' || currentPlan === 'trial'}
              className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400"
            >
              {currentPlan === planKey ? t('currentPlanLabel') : t('changePlan')}
            </button>
          </div>
        ))}
      </div>

      {isAdmin && accountStatus === 'active' && currentPlan !== 'trial' && (
          <div className="mt-12 pt-8 border-t border-gray-200">
              <h2 className="text-xl font-bold text-gray-800 mb-4">{t('cancellation.title')}</h2>
              <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                  <p className="text-gray-700 mb-4">
                      {t('cancellation.description')}
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-6">
                      {t('cancellation.points', { returnObjects: true }).map((point, i) => (
                          <li key={i}>{point}</li>
                      ))}
                  </ul>
                  <button
                      onClick={handleCancelRequest}
                      className="border border-red-600 text-red-600 font-bold py-2 px-6 rounded-lg hover:bg-red-50 transition-colors shadow-sm"
                  >
                      {t('cancellation.button')}
                  </button>
              </div>
          </div>
      )}

      {/* Cancellation Confirmation Modal */}
      <Modal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={confirmCancelSubscription}
          title={t('cancellation.modalTitle')}
          confirmLabel={t('cancellation.confirmButton')}
          cancelLabel={t('cancellation.cancelButton')}
          isConfirmDisabled={!hasUnderstoodCancellation}
      >
          <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-md space-y-2 text-sm">
                  <p><strong>{t('cancellation.currentPlanLabel')}:</strong> {currentPlanDetails.name}</p>
                  <p><strong>{t('cancellation.endDateLabel')}:</strong> {getNextBillingDateString()}</p>
                  <p><strong>{t('cancellation.estimatedAmountLabel')}:</strong> {estimatedAmount.toLocaleString()}円（税別）</p>
              </div>

              <div>
                  <h4 className="font-bold text-gray-800 mb-2">{t('cancellation.restrictionsTitle')}</h4>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                      {t('cancellation.restrictions', { returnObjects: true }).map((res, i) => (
                          <li key={i}>{res}</li>
                      ))}
                  </ul>
              </div>

              <div className="pt-4 border-t border-gray-100">
                  <label className="flex items-center space-x-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="h-5 w-5 text-blue-600 rounded" 
                        checked={hasUnderstoodCancellation}
                        onChange={(e) => setHasUnderstoodCancellation(e.target.checked)}
                      />
                      <span className="text-gray-800 font-medium">{t('cancellation.checkboxLabel')}</span>
                  </label>
              </div>
          </div>
      </Modal>

      <Modal
          isOpen={isConfirmModalOpen}
          onClose={closeConfirmModal}
          onConfirm={confirmPlanChange}
          title={t('modalTitle')}
      >
          <p>{confirmModalMessage}</p>
      </Modal>

      <NotificationModal
          isOpen={isNotificationModalOpen}
          onClose={closeNotificationModal}
          title={notificationModalContent.title}
      >
          <p>{notificationModalContent.message}</p>
      </NotificationModal>
    </div>
  );
};

export default Subscription;
